import {DropDisplayObject, DropElement} from "goldenrock/components/reeldrop/reel-drop-component";
import {DisplayObject} from "pixi.js";
import FlexiVideo from "slotworks/graphics/elements/flexi-video";
import Layers from "slotworks/graphics/layers/layers";
import gameState from "slotworks/model/game-state";
import SpinRecord from "slotworks/model/gameplay/records/spin-record";
import Contract from "slotworks/utils/contracts/contract";
import Parallel from "slotworks/utils/contracts/parallel";
import Sequence from "slotworks/utils/contracts/sequence";
import {Easing, Tween} from "slotworks/utils/tween";
import sgScatterAnimation from "./sg-scatter-animation";
import backgroundTint from "./show-win-lines/background-tint";
import sgWildsComponent from "./wilds/sg-wilds-component";
import { Services } from "slotworks/services/services";

const HIDE_DURATION: number = 1000;
const BACKGROUND_HIDE_DELAY: number = 1000;
const BACKGROUND_HIDE_DURATION: number = 2000;
const TARNSITION_VIDEO_ID: string = "bonus_transition";

class SGFreespinTransitionComponent {
    private transitionInVideo: FlexiVideo;
    private dropDisplayObjects: DropDisplayObject[];
    private reelFrameAnimTween: {alpha: number;} = {alpha: 1};
    private hideElements: DropElement[];
    private sandAnimation: PIXI.spine.Spine;

    private static SAND_ANIMATION:string =  "sand_animation";
    private static BONUS_TRANSITION_SAND:string = "bonus-transition-sand"

    public init(hideElements: DropElement[]): void {
        this.hideElements = hideElements;

        this.sandAnimation = Layers.SymbolAnimations.getSpine(SGFreespinTransitionComponent.SAND_ANIMATION);
        this.sandAnimation.visible = false;
    }

    public playSandAnimation(): Contract<void> {
        this.sandAnimation.visible = true;
        this.sandAnimation.state.clearListeners();
        this.sandAnimation.state.setAnimation(0, SGFreespinTransitionComponent.BONUS_TRANSITION_SAND, false);

        return new Contract<void>((resolve) => {
            this.sandAnimation.state.addListener({
                complete: () => {
                    this.sandAnimation.state.clearListeners();
                    this.sandAnimation.visible = false;
                    resolve();
                }
            });
        });
    }


    public transitionIn(): Contract<void> {

        const record = gameState.getCurrentGame().getCurrentRecord() as SpinRecord;

        backgroundTint.show();
        backgroundTint.updateDepth(record.results);

        return new Sequence([
            () => new Parallel([
                () => sgScatterAnimation.getScatterBonusTriggerAnimationContract(),
                () => this.playWildsBonusIntro()
            ]),
            () => new Parallel([
                () => this.playSandAnimation(),
                () => Contract.wrap(() => Services.sound.customEvent("enter-freespins"))
            ]),
            () => this.playHideReelsTweenAnimation(),
            () => new Parallel([
                () => this.playTransitionInVideo(),
                () => this.hideBackground(),
                () => Contract.wrap(() => {backgroundTint.hide();})
            ])
        ]);
    }

    private playWildsBonusIntro(): Contract<void> {
        return new Parallel([
            () => sgWildsComponent.playBonusItro([
                sgWildsComponent.isExpandedReel(1),
                sgWildsComponent.isExpandedReel(2),
                sgWildsComponent.isExpandedReel(3)
            ]),
            () => Contract.wrap(() => Services.sound.customEvent("free-spin-trigger"))
        ]);
    }

    private playHideReelsTweenAnimation(): Contract<void> {
        return new Contract<void>((resolve) => {
            this.resetDropElements();
            Layers.SmartButton.hide();
            this.hideReels(0, Easing.Quadratic.In).execute();
            resolve(null);
        });
    }

    public resetReels(): Contract<void> {
        return new Contract((resolve) => {
            this.getDropDisplayObjects().forEach((element) => {
                element.setVisible(true);
                element.displayObject.alpha = 1;
            });
            resolve();
        });
    }

    protected hideReels(to: number, easing: (k: number) => number): Contract<void> {
        this.reelFrameAnimTween.alpha = 1;
        return new Contract<void>((resolve) => {

            const elements = this.getDropDisplayObjects();

            const tween = new Tween(this.reelFrameAnimTween);
            tween.to({alpha: to}, HIDE_DURATION);
            tween.easing(easing);
            tween.onStart(() => {
                elements.forEach((element: DropDisplayObject) => {
                    element.displayObject.cacheAsBitmap = true;
                    element.setVisible(true);
                });
            });
            tween.onUpdate(() => {
                elements.forEach((element: DropDisplayObject) => {
                    element.displayObject.alpha = this.reelFrameAnimTween.alpha;
                });
            });
            tween.onComplete(() => {
                elements.forEach((element: DropDisplayObject) => {
                    element.setVisible(to !== 0);
                    element.displayObject.cacheAsBitmap = false;
                });
                resolve(null);
            });
            tween.start();
        });
    }

    private resetDropElements() {
        this.getDropDisplayObjects().forEach((element: DropDisplayObject) => {
            element.destroy();
        });
        this.dropDisplayObjects = null;
    }

    private getDropDisplayObjects() {
        if (!this.dropDisplayObjects) {
            this.dropDisplayObjects = [
                new DropDisplayObject(Layers.MatrixContent.container)
            ];

            this.hideElements.forEach((element: DropElement) => {
                const displayObject: DisplayObject = element.spriteName ?
                    element.layer.getSprite(element.spriteName) :
                    element.layer.container;
                this.dropDisplayObjects.push(new DropDisplayObject(displayObject));
            });
        }

        return this.dropDisplayObjects;
    }

    public reset(): Contract<void> {
        return new Parallel([
            () => Layers.MatrixForeground.setScene("default"),
            () => Contract.wrap(() => {
                this.transitionInVideo.seek(0);
                const backgrounds: any = document.querySelectorAll("#Background #default .background_image");
                for (const background of backgrounds) {
                    background.style.opacity = "1";
                }
            })
        ]);
    }

    private hideBackground(): Contract<void> {
        return new Contract((resolve) => {
            const backgrounds: any = document.querySelectorAll("#Background #default .background_image");
            const tweenObject = {opacity: 1};
            new Tween(tweenObject)
                .delay(BACKGROUND_HIDE_DELAY)
                .onUpdate(() => {
                    for (const background of backgrounds) {
                        background.style.opacity = `${tweenObject.opacity}`;
                    }
                })
                .onComplete(() => resolve())
                .to({opacity: 0}, BACKGROUND_HIDE_DURATION)
                .start();
        });
    }

    private playTransitionInVideo(): Contract<void> {
        return new Contract((resolve) => {
            this.transitionInVideo = Layers.Background.getFlexiVideo(TARNSITION_VIDEO_ID);
            this.transitionInVideo.setVisible(true);
            this.transitionInVideo.playContract(0, true).then(()=>{
                Layers.Background.jumpToScene("freespins"),
                resolve();
            });
        });
    }


}

export default new SGFreespinTransitionComponent();
